/*
 * `import type`, not a value import — this is erased at compile time, so naming
 * the type here does not pull laravel-echo into the bundle. The libraries
 * themselves arrive through the dynamic import in `load()` below.
 */
import type Echo from 'laravel-echo'
import { getToken } from './api'
import { resolveTransport } from './realtimeTransport'

/**
 * The websocket connection to Reverb.
 *
 * ## What comes over it
 *
 * Signals, not data. Every event carries an id and a reason — never a
 * submission's contents. The client reacts by invalidating the matching query,
 * and TanStack refetches through the REST API, which is role-scoped server-side
 * and covered by tests/Feature/RoleVisibilityTest.php.
 *
 * That means a socket bug cannot leak a field, because no field is ever on the
 * wire. See app/Events/QueueChanged.php for the same reasoning from the other
 * end.
 *
 * ## It is an accelerator, not a dependency
 *
 * Polling stays on (see ./live.ts). If Reverb is not running — and on a plant
 * box a daemon that died overnight is a real scenario — the app does not
 * degrade past "updates arrive within ten seconds instead of instantly", and
 * nothing tells the user anything is wrong because operationally nothing is.
 * That is the whole reason the polling was built first.
 *
 * ## Loaded on demand
 *
 * laravel-echo and pusher-js are ~87 KB, and statically importing them put both
 * on the critical path of every page — including the login screen, where there
 * is no session and therefore nothing to subscribe to. That quietly undid part
 * of the earlier bundle work.
 *
 * They are now behind a dynamic `import()`, so they download when the first
 * authenticated screen mounts and never for a user sitting at the sign-in form.
 * The cost is that `getEcho()` is asynchronous; callers were few, and the one
 * that matters already lives in an effect.
 */

type EchoInstance = InstanceType<typeof Echo>

/**
 * The in-flight or settled connection.
 *
 * A promise rather than the instance, so that two components mounting in the
 * same tick share one download and one socket instead of racing to create two.
 */
let echoPromise: Promise<EchoInstance | null> | null = null

/** Whether the app was built with Reverb configured at all. */
export function realtimeConfigured(): boolean {
  return Boolean(import.meta.env.VITE_REVERB_APP_KEY)
}

/** Reads the environment and the page URL; the decision itself is pure. */
function transport() {
  return resolveTransport({
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    scheme: import.meta.env.VITE_REVERB_SCHEME,
    host: import.meta.env.VITE_REVERB_HOST,
    port: import.meta.env.VITE_REVERB_PORT,
  })
}

/**
 * The shared connection, created on first use.
 *
 * Resolves to null when Reverb is not configured **or when the libraries fail
 * to load** — a chunk that 404s after a deploy, or a device that dropped its
 * connection mid-download. Every call site then degrades to polling by doing
 * nothing, rather than by throwing inside an effect.
 */
export function getEcho(): Promise<EchoInstance | null> {
  if (!realtimeConfigured()) return Promise.resolve(null)
  if (echoPromise) return echoPromise

  echoPromise = load().catch((error) => {
    console.error('[KaProLean] Live updates unavailable; falling back to polling.', error)

    // Cleared so a later mount can retry rather than being stuck with a
    // permanently rejected promise.
    echoPromise = null

    return null
  })

  return echoPromise
}

async function load(): Promise<EchoInstance> {
  const [{ default: EchoClient }, { default: Pusher }] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js'),
  ])

  // laravel-echo expects Pusher on window; it has no other way to find the
  // client. Set here rather than at module scope now that the import is lazy.
  ;(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher

  const { host, port, useTLS } = transport()

  return new EchoClient({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: useTLS,
    /*
     * One transport, not both.
     *
     * Offering `['ws', 'wss']` lets pusher-js attempt the plaintext socket
     * first. On an HTTPS page the browser blocks that as mixed content, so
     * every connection burned a failed attempt and filled the console with
     * alternating ws/wss errors before settling. Pinning the transport to the
     * one that can actually work removes both the delay and the noise — and
     * makes a silent downgrade impossible.
     */
    enabledTransports: useTLS ? ['wss'] : ['ws'],

    /*
     * Private channels are authorised by the API, and this client carries a
     * bearer token rather than a session cookie — so the header has to be set
     * explicitly. The matching half is in bootstrap/app.php, where
     * `/broadcasting/auth` is registered behind `auth:sanctum` instead of the
     * default `web` middleware.
     *
     * Read at call time, not at module load: the token does not exist yet when
     * this file is first evaluated, and it changes on login.
     */
    authEndpoint: '/broadcasting/auth',
    auth: {
      get headers() {
        return { Authorization: `Bearer ${getToken() ?? ''}` }
      },
    },
  }) as EchoInstance
}

/**
 * Drops the connection and forgets it.
 *
 * Called on logout: the socket is authorised with the old user's token, and
 * leaving it open on a shared shop-floor terminal would keep pushing the
 * previous person's channels to the next person's browser.
 *
 * Stays synchronous for the caller, but handles the case where the libraries
 * are still downloading: the reference is dropped immediately so a fresh login
 * builds a new socket, and the one in flight disconnects itself the moment it
 * arrives rather than lingering with the old user's credentials.
 */
export function disconnectRealtime(): void {
  const pending = echoPromise
  echoPromise = null

  pending?.then((echo) => echo?.disconnect()).catch(() => {})
}
